/**
 * SocialFeed - Instagram-style feed of scent reports
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSocialStore } from '@/stores/useSocialStore';
import { ReportCard } from './ReportCard';
import type { FeedType } from '@/types/social';

interface SocialFeedProps {
  type?: FeedType;
  userId?: string;
  onUserClick?: (userId: string) => void;
  onReportClick?: (reportId: string) => void;
}

export function SocialFeed({ type = 'explore', userId, onUserClick, onReportClick }: SocialFeedProps) {
  const { feed, feedLoading, feedError, hasMore, fetchFeed, resetFeed } = useSocialStore();
  const [activeTab, setActiveTab] = useState<'latest' | 'featured' | 'verified'>('latest');
  const [page, setPage] = useState(0);

  useEffect(() => {
    resetFeed();
    setPage(0);
    fetchFeed({
      type,
      userId,
      featured: activeTab === 'featured',
      verified: activeTab === 'verified',
    });
  }, [type, userId, activeTab, fetchFeed, resetFeed]);

  const loadMore = () => {
    if (hasMore && !feedLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchFeed({
        type,
        userId,
        featured: activeTab === 'featured',
        verified: activeTab === 'verified',
      }, nextPage);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      {/* Tabs */}
      {type === 'explore' && (
        <div className="sticky top-0 z-10 bg-app-bg/80 backdrop-blur-xl border-b border-glass-border mb-4">
          <div className="flex">
            {[
              { key: 'latest', label: '🆕 Latest', color: 'hud-primary' },
              { key: 'featured', label: '🌟 Featured', color: 'pink' },
              { key: 'verified', label: '✅ Verified', color: 'teal' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={cn(
                  'flex-1 py-3 text-sm font-medium transition-colors relative',
                  activeTab === tab.key ? 'text-white' : 'text-text-secondary hover:text-white'
                )}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <motion.div
                    layoutId="feedTab"
                    className={cn('absolute bottom-0 left-0 right-0 h-0.5 bg-hud-primary')}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="space-y-4 px-4">
        {feed.map((report, index) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <ReportCard
              report={report}
              onUserClick={onUserClick}
              onReportClick={onReportClick}
            />
          </motion.div>
        ))}

        {/* Loading */}
        {feedLoading && (
          <div className="py-8 text-center">
            <div className="inline-flex items-center gap-2 text-text-secondary">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-5 h-5 border-2 border-hud-primary border-t-transparent rounded-full"
              />
              <span>Loading reports...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {feedError && (
          <div className="py-8 text-center text-red-400">
            Error loading feed: {feedError}
          </div>
        )}

        {/* Empty state */}
        {!feedLoading && feed.length === 0 && (
          <div className="py-16 text-center">
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-semibold text-white mb-2">No reports yet</h3>
            <p className="text-text-secondary mb-4">Be the first to file a scent report!</p>
          </div>
        )}

        {/* Load more */}
        {hasMore && feed.length > 0 && (
          <button
            onClick={loadMore}
            disabled={feedLoading}
            className={cn(
              'w-full py-3 text-center text-hud-primary font-medium',
              'hover:bg-hud-primary/10 rounded-xl transition-colors',
              'disabled:opacity-50'
            )}
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
